import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IntlMessageFormat } from 'intl-messageformat';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { defaultLanguage, languages } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { getFullPath } from '../lib/get-full-path';
import { getPathType } from '../lib/get-path-type';
import { NetworkInterceptorService } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { BrowserService } from './browser.service';
import { LanguageService } from './language.service';
import { LazyLoadedSentryService } from './lazy-loaded-sentry-service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  private networkService = inject(NetworkInterceptorService);
  private lazyLoadedSentryService = inject(LazyLoadedSentryService);
  private platformId = inject(PLATFORM_ID);
  private languageService = inject(LanguageService);
  private loader = inject(ObservableDataLoaderService);
  private http = inject(HttpClient);
  private growthBook = inject(GROWTHBOOK);
  private config = inject(PICFLOW_CONFIG);
  private transferState = inject(TransferStateService);
  private browserService = inject(BrowserService);
  private router = inject(Router, { optional: true });
  private route = inject(ActivatedRoute, { optional: true });

  private handleTranslation(
    template: string,
    params: { [key: string]: string | number },
    backupTemplate?: string
  ): string {
    const paramsWithB = {
      ...(params || {}),
      b: (text: string) => `<b>${text}</b>`,
    };
    if (!params) return template;
    try {
      return new IntlMessageFormat(template).format(paramsWithB) as string;
    } catch (error) {
      console.error(error);
      this.lazyLoadedSentryService
        .load()
        .then(sentryService => sentryService.captureException('translation_error', { template, params }));
      return new IntlMessageFormat(backupTemplate).format(paramsWithB) as string;
    }
  }

  private actions$ = merge(
    this.router ? this.router.events : of(undefined),
    this.networkService.request$,
    this.networkService.response$
  );

  private onIdle$ = this.actions$.pipe(
    debounceTime(1000),
    map(() => true),
    take(1),
    shareReplay(1)
  );

  private doTranslateAll = this.router
    ? this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          if (this.browserService.isTenantDomain()) return true;
          const path = getFullPath(this.route?.snapshot);
          if (path[0] === 'a') return true;
          return false;
        })
      )
    : of(false);

  private allTranslations = toSignal(
    isPlatformServer(this.platformId)
      ? of(undefined)
      : this.onIdle$.pipe(
          filter(Boolean),
          switchMap(() =>
            this.doTranslateAll.pipe(
              filter(Boolean),
              switchMap(() =>
                this.languageService.currentLanguage$.pipe(
                  distinctUntilChanged(),
                  filter(lang => lang && lang !== defaultLanguage),
                  switchMap(lang => {
                    return this.http
                      .get<{ [key: string]: string }>(`assets/i18n/${lang}/all.json?v=${this.config.version}`)
                      .pipe(
                        catchError(() => of({} as { [key: string]: string })),
                        map(translations => ({ lang, translations }))
                      );
                  })
                )
              )
            )
          )
        )
  );

  public translate(
    keyWithNamespace: { ___: string; defaultText: string },
    params?: { [key: string]: string | number }
  ): Observable<string> {
    if (!keyWithNamespace) return of('');

    const language = this.languageService.currentLanguage();
    const namespace = keyWithNamespace.___.split('.')[0];
    const key = keyWithNamespace.___.split('.').slice(1).join('.');
    if (this.growthBook.isOn('translation-debug-mode')) return of(keyWithNamespace.___);

    if (language === defaultLanguage || !language || !namespace || !key) {
      return of(this.handleTranslation(keyWithNamespace.defaultText, params));
    }

    const i18nStatus = languages.find(lang => lang.lang === language)?.i18n;
    const pathType = getPathType(getFullPath(this.route?.snapshot), this.browserService.isTenantDomain());
    if (!i18nStatus) return of(this.handleTranslation(keyWithNamespace.defaultText, params));
    if ((pathType === 'public' || pathType === 'auth') && !i18nStatus.website) {
      return of(this.handleTranslation(keyWithNamespace.defaultText, params));
    }
    if (pathType === 'app' && !i18nStatus.app) {
      return of(this.handleTranslation(keyWithNamespace.defaultText, params));
    }
    if (pathType === 'tenant-domain' && !i18nStatus.tenantDomain) {
      return of(this.handleTranslation(keyWithNamespace.defaultText, params));
    }

    const allTranslations = this.allTranslations();
    if (allTranslations && allTranslations.lang === language) {
      return of(
        this.handleTranslation(
          allTranslations.translations[namespace][key] || keyWithNamespace.defaultText,
          params,
          keyWithNamespace.defaultText
        )
      );
    }

    const cacheKey = `translations-${language}-${namespace}`;
    return this.transferState
      .withStateTransferObservable(cacheKey, () =>
        this.loader.get(cacheKey, () =>
          this.http
            .get<{ [key: string]: string }>(`assets/i18n/${language}/${namespace}.json?v=${this.config.version}`)
            .pipe(catchError(() => of({} as { [key: string]: string })))
        )
      )
      .pipe(
        take(1),
        map(translations => {
          return this.handleTranslation(
            translations[key] || keyWithNamespace.defaultText,
            params,
            keyWithNamespace.defaultText
          );
        })
      );
  }
}
